/**
 * java.awt.Color — faithful port.
 *
 * Immutable. Caches its CSS string, since the render path asks for it thousands of
 * times per frame and Colors are allocated freely by the game code (as in Java).
 */
export class Color {
  readonly r: number;
  readonly g: number;
  readonly b: number;
  readonly a: number; // 0..255, like Java

  private css: string | null = null;

  constructor(r: number, g: number, b: number, a: number = 255) {
    this.r = clampByte(r);
    this.g = clampByte(g);
    this.b = clampByte(b);
    this.a = clampByte(a);
  }

  getRed(): number { return this.r; }
  getGreen(): number { return this.g; }
  getBlue(): number { return this.b; }
  getAlpha(): number { return this.a; }

  /** CSS color string, memoized. */
  toCss(): string {
    if (this.css === null) {
      this.css = this.a >= 255
        ? `rgb(${this.r},${this.g},${this.b})`
        : `rgba(${this.r},${this.g},${this.b},${(this.a / 255).toFixed(4)})`;
    }
    return this.css;
  }

  // ---- Java statics ----

  static readonly black = new Color(0, 0, 0);
  static readonly BLACK = Color.black;
  static readonly white = new Color(255, 255, 255);
  static readonly WHITE = Color.white;
  static readonly red = new Color(255, 0, 0);
  static readonly RED = Color.red;
  static readonly gray = new Color(128, 128, 128);
  static readonly GRAY = Color.gray;

  /**
   * java.awt.Color.RGBtoHSB — returns [hue, saturation, brightness], each 0..1.
   */
  static RGBtoHSB(r: number, g: number, b: number, out?: number[]): number[] {
    const hsb = out ?? new Array<number>(3);
    let cmax = r > g ? r : g;
    if (b > cmax) cmax = b;
    let cmin = r < g ? r : g;
    if (b < cmin) cmin = b;

    const brightness = cmax / 255.0;
    const saturation = cmax !== 0 ? (cmax - cmin) / cmax : 0;

    let hue: number;
    if (saturation === 0) {
      hue = 0;
    } else {
      const redc = (cmax - r) / (cmax - cmin);
      const greenc = (cmax - g) / (cmax - cmin);
      const bluec = (cmax - b) / (cmax - cmin);
      if (r === cmax) hue = bluec - greenc;
      else if (g === cmax) hue = 2.0 + redc - bluec;
      else hue = 4.0 + greenc - redc;
      hue = hue / 6.0;
      if (hue < 0) hue = hue + 1.0;
    }
    hsb[0] = hue;
    hsb[1] = saturation;
    hsb[2] = brightness;
    return hsb;
  }

  /**
   * java.awt.Color.HSBtoRGB — h/s/b in 0..1, returns packed 0xRRGGBB.
   */
  static HSBtoRGB(hue: number, saturation: number, brightness: number): number {
    let r = 0, g = 0, b = 0;
    if (saturation === 0) {
      r = g = b = Math.trunc(brightness * 255.0 + 0.5);
    } else {
      const h = (hue - Math.floor(hue)) * 6.0;
      const f = h - Math.floor(h);
      const p = brightness * (1.0 - saturation);
      const q = brightness * (1.0 - saturation * f);
      const t = brightness * (1.0 - saturation * (1.0 - f));
      switch (Math.trunc(h)) {
        case 0:
          r = Math.trunc(brightness * 255.0 + 0.5);
          g = Math.trunc(t * 255.0 + 0.5);
          b = Math.trunc(p * 255.0 + 0.5);
          break;
        case 1:
          r = Math.trunc(q * 255.0 + 0.5);
          g = Math.trunc(brightness * 255.0 + 0.5);
          b = Math.trunc(p * 255.0 + 0.5);
          break;
        case 2:
          r = Math.trunc(p * 255.0 + 0.5);
          g = Math.trunc(brightness * 255.0 + 0.5);
          b = Math.trunc(t * 255.0 + 0.5);
          break;
        case 3:
          r = Math.trunc(p * 255.0 + 0.5);
          g = Math.trunc(q * 255.0 + 0.5);
          b = Math.trunc(brightness * 255.0 + 0.5);
          break;
        case 4:
          r = Math.trunc(t * 255.0 + 0.5);
          g = Math.trunc(p * 255.0 + 0.5);
          b = Math.trunc(brightness * 255.0 + 0.5);
          break;
        case 5:
          r = Math.trunc(brightness * 255.0 + 0.5);
          g = Math.trunc(p * 255.0 + 0.5);
          b = Math.trunc(q * 255.0 + 0.5);
          break;
      }
    }
    return (r << 16) | (g << 8) | b;
  }

  /** java.awt.Color.getHSBColor */
  static getHSBColor(h: number, s: number, b: number): Color {
    const packed = Color.HSBtoRGB(h, s, b);
    return new Color((packed >> 16) & 0xff, (packed >> 8) & 0xff, packed & 0xff);
  }
}

function clampByte(v: number): number {
  v = Math.round(v);
  if (v < 0) return 0;
  if (v > 255) return 255;
  return v;
}
